import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import EntityStatusBadge from '#/modules/commonComponents/table/EntityStatusBadge';
import BomCommandBar from '#/modules/momMgmt/settings/bom/components/BomCommandBar';
import {
  BOM_USAGE_NOT_USED,
  countUsedQty,
  getBomExtraMaterials,
  getBomGroupMaterials,
} from '#/modules/momMgmt/settings/bom/helper/bomGroupsForm';
import { formatBomStatusLabel } from '#/modules/momMgmt/settings/bom/helper/bomStatus';
import { formatDateTimeOrDash } from '#/utils/date';
import { formatDisplayQty } from '#/utils/formatNumber';

function displayOrDash(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
}

function HistoryField({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{value}</p>
    </div>
  );
}

HistoryField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

function BomMainInfoCard({ detail, actions = null }) {
  const status = detail?.status;

  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">
                {displayOrDash(detail?.code)}
              </h1>
              {status ? (
                <EntityStatusBadge status={status} label={formatBomStatusLabel(status)} />
              ) : null}
            </div>
            <p className="mt-1 text-sm text-gray-600">
              {displayOrDash(detail?.name)}
              {detail?.version ? ` · ${detail.version}` : ''}
            </p>
          </div>
          {actions ? (
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              {actions}
            </div>
          ) : null}
        </div>

        <div
          className={cn(
            'mt-4 grid grid-cols-1 gap-x-4 gap-y-3 border-t border-slate-200 pt-4',
            'sm:grid-cols-2 md:grid-cols-4',
          )}
        >
          <HistoryField
            label="建立"
            value={formatDateTimeOrDash(
              detail?.record?.createdAt ?? detail?.createdAt,
            )}
          />
          <HistoryField
            label="修改"
            value={formatDateTimeOrDash(
              detail?.record?.updatedAt ?? detail?.updatedAt,
            )}
          />
          <HistoryField label="版本" value={displayOrDash(detail?.version)} />
          <HistoryField label="狀態" value={formatBomStatusLabel(status)} />
          <div className="sm:col-span-2 md:col-span-4">
            <p className="text-xs text-gray-500">備註</p>
            <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
              {displayOrDash(detail?.remark)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

BomMainInfoCard.propTypes = {
  detail: PropTypes.object.isRequired,
  actions: PropTypes.node,
};

function BomGroupsReadonlySection({ groups = [] }) {
  if (!groups.length) {
    return (
      <div className="receipt-card-elevate">
        <div className="receipt-card-surface p-5">
          <h2 className="form-block__title">群組／部位</h2>
          <p className="mt-2 text-sm text-muted-foreground">尚無群組</p>
        </div>
      </div>
    );
  }

  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface p-5 space-y-4">
        <h2 className="form-block__title">群組／部位</h2>
        {groups.map((group, groupIndex) => (
          <div
            key={String(group?.id ?? `group-${groupIndex}`)}
            className="rounded border border-slate-200 p-3"
          >
            <p className="text-sm font-semibold text-gray-900">
              {displayOrDash(group?.name)}
            </p>
            <ul className="mt-2 space-y-2">
              {getBomGroupMaterials(group).map((material, materialIndex) => {
                const mtr = material?.mtr;
                const mtrNo = mtr?.mtrNo ?? material?.mtrNo;
                const mtrName = mtr?.name ?? material?.mtrName;
                const positions = material?.positions ?? [];
                const usedQty = countUsedQty(positions);
                return (
                  <li
                    key={String(
                      material?.mtrId
                      ?? material?.sourceMtrId
                      ?? mtr?.id
                      ?? `mtr-${materialIndex}`,
                    )}
                    className="rounded bg-slate-50 px-3 py-2 text-sm"
                  >
                    <p className="font-medium text-gray-900">
                      {[mtrNo, mtrName].filter(Boolean).join(' - ') || '—'}
                      <span className="ml-2 inline-flex items-baseline space-x-1">
                        <span className="text-xs text-muted-foreground">使用部位</span>
                        <span className="text-sm font-medium tabular-nums text-slate-800">
                          {usedQty}
                        </span>
                        <span className="text-xs text-muted-foreground">／</span>
                        <span className="text-sm font-medium tabular-nums text-slate-800">
                          {positions.length}
                        </span>
                      </span>
                    </p>
                    {positions.length > 0 ? (
                      <ul className="mt-1 flex flex-wrap gap-1.5">
                        {positions.map((position, positionIndex) => (
                          <li
                            key={String(position?.id ?? `pos-${positionIndex}`)}
                            className={cn(
                              'rounded px-2 py-0.5 text-xs',
                              position?.usageType === BOM_USAGE_NOT_USED
                                ? 'bg-slate-200 text-slate-600'
                                : 'bg-emerald-100 text-emerald-800',
                            )}
                          >
                            {displayOrDash(position?.name)}
                            {position?.usageType === BOM_USAGE_NOT_USED ? '（未使用）' : ''}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-1 text-xs text-muted-foreground">無部位</p>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

BomGroupsReadonlySection.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.object),
};

function BomExtraMaterialsReadonlySection({ extraMaterials = [] }) {
  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface p-5">
        <h2 className="form-block__title">額外物料</h2>
        {extraMaterials.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">尚無額外物料</p>
        ) : (
          <div className="mt-3 overflow-x-auto rounded border border-slate-200">
            <table className="w-full min-w-[28rem] text-left text-sm">
              <thead className="bg-slate-100 text-xs text-gray-600">
                <tr>
                  <th className="px-2 py-1.5 font-medium">料號</th>
                  <th className="px-2 py-1.5 font-medium">料名</th>
                  <th className="px-2 py-1.5 font-medium">數量</th>
                </tr>
              </thead>
              <tbody>
                {extraMaterials.map((item, index) => {
                  const mtr = item?.mtr;
                  return (
                    <tr
                      key={String(item?.mtrId ?? mtr?.id ?? `extra-${index}`)}
                      className="border-t border-slate-200"
                    >
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        {displayOrDash(mtr?.mtrNo ?? item?.mtrNo)}
                      </td>
                      <td className="px-2 py-1.5">
                        {displayOrDash(mtr?.name ?? item?.mtrName)}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        {formatDisplayQty(item?.qty)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

BomExtraMaterialsReadonlySection.propTypes = {
  extraMaterials: PropTypes.arrayOf(PropTypes.object),
};

const BomBlock = ({ detail, onRefetch, showActions = false }) => (
  <div className="space-y-4">
    <BomMainInfoCard
      detail={detail}
      actions={
        showActions ? (
          <BomCommandBar
            key={`${detail.id}-${detail.record?.updatedAt ?? detail.updatedAt ?? ''}`}
            detail={detail}
            onRefetch={onRefetch}
          />
        ) : null
      }
    />
    <BomExtraMaterialsReadonlySection
      extraMaterials={getBomExtraMaterials(detail)}
    />
    <BomGroupsReadonlySection groups={detail?.groups ?? []} />
  </div>
);

BomBlock.propTypes = {
  detail: PropTypes.object.isRequired,
  onRefetch: PropTypes.func,
  showActions: PropTypes.bool,
};

export default BomBlock;
